import {Inject, Injectable, NgZone, PLATFORM_ID} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {EMPTY, firstValueFrom, Observable, Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {fetchEventSource} from '@microsoft/fetch-event-source';
import {environment} from '../../../environments/environment';
import {EventSourceInterface} from '../interfaces/event-source.interface';

type Closeable = {close(): void};
interface MercureCredential {token: string; expiresAt: number;}
interface CredentialRequest {
  promise: Promise<MercureCredential>;
  cancel: Subject<void>;
  value?: MercureCredential;
}
class ForbiddenStream extends Error {}

@Injectable({providedIn: 'root'})
export class EventSourceService {
  private readonly reconnected = new Subject<readonly string[]>();
  public readonly reconnected$ = this.reconnected.asObservable();
  public eventSources: Closeable[] = [];
  private streams = new Map<string, Subject<EventSourceInterface<any>>>();
  private observables = new Map<string, Observable<EventSourceInterface<any>>>();
  private sources = new Map<string, Closeable>();
  private credentials = new Map<string, CredentialRequest>();

  constructor(@Inject(PLATFORM_ID) private readonly platformId: object,
              private readonly ngZone: NgZone, private readonly http: HttpClient) {}

  public listenSingleTopic<T>(topic: string, closable = true, ngswBypass = true): Observable<EventSourceInterface<T>> {
    return this.listen<T>([topic], closable, ngswBypass);
  }

  public listenArrayOfTopics<T>(topics: string[], closable = true, ngswBypass = true): Observable<EventSourceInterface<T>> {
    return this.listen<T>(topics.map(topic => topic.replace(/^topic=/, '')), closable, ngswBypass);
  }

  private url(topics: string[], bypass: boolean): string {
    const params = new URLSearchParams();
    topics.forEach(topic => params.append('topic', topic));
    if (bypass) params.set('ngsw-bypass', 'true');
    return environment.mercureUrl + '?' + params.toString();
  }

  private listen<T>(topics: string[], closable: boolean, bypass: boolean): Observable<EventSourceInterface<T>> {
    const url = this.url(topics, bypass);
    const previous = this.observables.get(url);
    if (previous) return previous;
    const stream = new Subject<EventSourceInterface<T>>();
    if (!isPlatformBrowser(this.platformId)) return EMPTY;
    this.streams.set(url, stream);
    const privateTopics = topics.filter(topic => /^\/(users\/[^/]+\/(notifications|conversations|unread-count)|conversations\/[^/]+\/messages)$/.test(topic));
    let closed = false;
    let controller: AbortController | undefined;
    let refresh: ReturnType<typeof setTimeout> | undefined;
    let retry: ReturnType<typeof setTimeout> | undefined;
    let lastEventId = '';
    let opened = false;
    const source = {close: () => {
      closed = true; controller?.abort(); clearTimeout(refresh); clearTimeout(retry);
    }};
    this.sources.set(url, source);
    let observers = 0;
    const observable = new Observable<EventSourceInterface<T>>(observer => {
      observers++;
      const listener = stream.subscribe(observer);
      return () => {
        listener.unsubscribe(); observers--;
        if (closable && observers === 0 && this.sources.get(url) === source) {
          source.close(); this.sources.delete(url); this.streams.delete(url); this.observables.delete(url);
          this.eventSources = this.eventSources.filter(item => item !== source);
        }
      };
    });
    this.observables.set(url, observable);
    if (closable) this.eventSources.push(source);
    const connect = async () => {
      if (closed) return;
      const connection = new AbortController();
      controller = connection;
      let connectionRefresh: ReturnType<typeof setTimeout> | undefined;
      let credential: MercureCredential | undefined;
      try {
        const headers: Record<string, string> = {};
        if (privateTopics.length) {
          credential = await this.authorize(privateTopics);
          if (closed) return;
          headers.Authorization = 'Bearer ' + credential.token;
          refresh = connectionRefresh = setTimeout(() => {
            connection.abort(); void connect();
          }, Math.max(1000, credential.expiresAt * 1000 - Date.now() - 60000 - Math.random() * 30000));
        }
        if (lastEventId) headers['Last-Event-ID'] = lastEventId;
        await fetchEventSource(url, {
          headers, signal: connection.signal, openWhenHidden: true,
          onopen: async response => {
            if (closed || connection.signal.aborted) return;
            if (response.status === 401 || response.status === 403) {
              this.invalidateCredential(privateTopics, credential);
              throw new ForbiddenStream('Mercure access denied');
            }
            if (!response.ok || !response.headers.get('content-type')?.includes('text/event-stream')) throw new Error('Mercure unavailable');
            if (opened) this.ngZone.run(() => this.reconnected.next(topics));
            opened = true;
          },
          onmessage: event => {
            if (closed || connection.signal.aborted) return;
            if (event.id) lastEventId = event.id;
            if (!event.data) return;
            try {
              const data = JSON.parse(event.data);
              const payload = data.summary && data.type === 'Announce' ? JSON.parse(data.summary) : data;
              this.ngZone.run(() => stream.next(payload));
            } catch { /* A malformed event must not clear the displayed collection. */ }
          },
          onclose: () => { throw new Error('Mercure connection closed'); },
          onerror: error => {
            if (error instanceof ForbiddenStream) throw error;
            return 2000 + Math.random() * 3000;
          },
        });
      } catch (error) {
        clearTimeout(connectionRefresh);
        if (!closed && controller === connection && !(error instanceof ForbiddenStream) && ![401, 403].includes((error as any)?.status)) retry = setTimeout(() => void connect(), 3000 + Math.random() * 5000);
      }
    };
    this.ngZone.runOutsideAngular(() => void connect());
    return observable;
  }

  private authorizationTopics(topics: string[]): string[] {
    return Array.from(new Set(topics.flatMap(topic => {
      const user = topic.match(/^\/users\/([^/]+)\/(notifications|conversations|unread-count)$/);
      return user ? ['notifications', 'conversations', 'unread-count'].map(name => `/users/${user[1]}/${name}`) : [topic];
    }))).sort();
  }

  private invalidateCredential(topics: string[], value: MercureCredential | undefined): void {
    const key = JSON.stringify(this.authorizationTopics(topics));
    // A rejected old connection must not discard a newer token or pending renewal.
    if (value && this.credentials.get(key)?.value === value) this.credentials.delete(key);
  }

  private authorize(requestedTopics: string[]): Promise<MercureCredential> {
    const topics = this.authorizationTopics(requestedTopics);
    const key = JSON.stringify(topics);
    const cached = this.credentials.get(key);
    if (cached && (!cached.value || cached.value.expiresAt * 1000 > Date.now() + 90000)) return cached.promise;

    const entry: CredentialRequest = {promise: undefined!, cancel: new Subject<void>()};
    entry.promise = firstValueFrom(this.http.post<MercureCredential>(environment.apiUrl + '/realtime/mercure-token', {topics}, {
      headers: new HttpHeaders({'x-cache-bypass': 'true'}),
    }).pipe(takeUntil(entry.cancel))).then(value => {
      entry.value = value;
      return value;
    }).catch(error => {
      if (this.credentials.get(key) === entry) this.credentials.delete(key);
      throw error;
    });
    this.credentials.set(key, entry);
    return entry.promise;
  }

  public closeArrayOfTopics(topics: string[], ngswBypass = true): void {
    const url = this.url(topics.map(topic => topic.replace(/^topic=/, '')), ngswBypass);
    const source = this.sources.get(url);
    const stream = this.streams.get(url);
    source?.close(); this.sources.delete(url);
    this.streams.delete(url); this.observables.delete(url);
    this.eventSources = this.eventSources.filter(item => item !== source);
    stream?.complete();
  }

  public closeAll(): void {
    this.sources.forEach(source => source.close());
    const streams = Array.from(this.streams.values());
    const credentials = Array.from(this.credentials.values());
    this.sources.clear(); this.streams.clear(); this.observables.clear(); this.credentials.clear(); this.eventSources = [];
    credentials.forEach(entry => { entry.cancel.next(); entry.cancel.complete(); });
    streams.forEach(stream => stream.complete());
  }
}
